import {
  Component,
  OnInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from "@angular/forms";
import * as _ from "underscore";
import { ActivatedRoute, Router } from "@angular/router";
import {
  GradingInputTypes,
  GradingInput,
  GradingHeaderInfo,
  GradingStagingData,
  GradingSheet,
  GradingStagingSheetStatus,
  GradingStagingSheet,
} from "../../../models/grading.model";
import { GradingService } from "../../../services/grading.service";
import { MatSelectChange, MatDatepickerInputEvent } from "@angular/material";
import { LoadingSpinnerService } from "../../../components/loading-spinner/loading-spinner.service";
import { ToastService } from "../../../components/toast/toast.service";
import { EtdrsComponent } from "../etdrs-grid/etdrs-grid.component";
import { DataService } from "../../../services/data.service";
import { GenericConfirmationModalComponent } from "../../../components/modal/modals/generic-confirmation-modal.component";
import { ValidationTypes } from "../../../models/meta-data.model";

@Component({
  templateUrl: "./grading.component.html",
  styleUrls: ["./grading.component.scss"],
  providers: [GradingService, DataService],
})
export class GradingComponent implements OnInit {
  public patientInfo: GradingHeaderInfo;
  public gradingSheets: GradingSheet[];
  public gradingInputTypes = GradingInputTypes;
  public formGroups: {
    [sheetName: string]: FormGroup;
  };
  public gradingSheetErrors: {
    [spreadsheetId: string]: string;
  } = {};
  public eyes = ["OD", "OS"];
  public eyeNameLookup = EyeNameLookup;
  public gradingStagingSheetStatus = GradingStagingSheetStatus;

  public todayDate = new Date();

  private requiredFields: { [field: string]: boolean };
  private gradingSheetInputs: { [dataGroupName: string]: Array<GradingInput> };

  private studyId: string;

  @ViewChildren(EtdrsComponent) etdrsGrids!: QueryList<EtdrsComponent>;
  @ViewChild("confirmEscalationModal")
  confirmEscalationModal: GenericConfirmationModalComponent;
  @ViewChild("confirmSubmitModal")
  confirmSubmitModal: GenericConfirmationModalComponent;

  @ViewChildren("sheetGroup") sheetGroups: QueryList<ElementRef>;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private loadingSpinnerService: LoadingSpinnerService,
    private gradingService: GradingService,
    private dataService: DataService,
    private router: Router
  ) {}

  async ngOnInit() {
    this.route.params.subscribe(async (params) => {
      const patientId = params["patientId"];
      const visitId = params["visitId"];
      this.studyId = params["studyId"];
      this.patientInfo = await this.gradingService.getGradingHeaderInfo(
        patientId,
        visitId,
        this.studyId
      );
      this.buildPage();
      this.requiredFields = {};
    });
  }

  private async buildPage() {
    this.loadingSpinnerService.start();
    try {
      const gradingSheetInputs = (this.gradingSheetInputs =
        await this.gradingService.getGradingSheetInputs(
          this.patientInfo.patientId,
          this.patientInfo.visitId,
          this.studyId
        ));

      this.gradingSheets = [];
      for (const dataGroupName in gradingSheetInputs) {
        if (gradingSheetInputs[dataGroupName]) {
          // set sheet status
          let locked = false;
          let lockedBy;
          let status = GradingStagingSheetStatus.pending;
          let spreadsheetId;
          if (gradingSheetInputs[dataGroupName].length > 0) {
            spreadsheetId = gradingSheetInputs[dataGroupName][0].spreadsheetId;
            if (gradingSheetInputs[dataGroupName][0]["sheetStatus"]) {
              status = gradingSheetInputs[dataGroupName][0]["sheetStatus"];
            }
            locked = gradingSheetInputs[dataGroupName][0]["isLocked"];
            if (locked) {
              lockedBy = gradingSheetInputs[dataGroupName][0]["lockedBy"];
            }
          }

          let sortOrder = 0;
          switch (spreadsheetId) {
            case "FAFGRADE":
              sortOrder = 20;
              break;
            case "CF-FFA":
              sortOrder = 10;
              break;
            case "OCTMAIN":
              sortOrder = 30;
              break;
            case "OCTMTSPECTRALIS":
              sortOrder = 40;
              break;
            case "OCTMTCIRRUS":
              sortOrder = 41;
              break;
          }

          for (let i = 0; i < gradingSheetInputs[dataGroupName].length; i++) {
            if (
              gradingSheetInputs[dataGroupName][i].gradingDropdownOptions &&
              gradingSheetInputs[dataGroupName][i].gradingDropdownOptions
                .length > 0
            ) {
              if (
                gradingSheetInputs[dataGroupName][i].columnId == "PHOTOQUALFAF"
              ) {
                gradingSheetInputs[dataGroupName][i].gradingDropdownOptions =
                  _.filter(
                    gradingSheetInputs[dataGroupName][i].gradingDropdownOptions,
                    (d) => d.deletedAt == null
                  );
              }
              gradingSheetInputs[dataGroupName][i].gradingDropdownOptions =
                _.sortBy(
                  gradingSheetInputs[dataGroupName][i].gradingDropdownOptions,
                  "fieldValue"
                );
            }
          }

          this.gradingSheets.push({
            inputs: _.sortBy(gradingSheetInputs[dataGroupName], "sortOrder"),
            title: dataGroupName,
            status: status,
            spreadsheetId: spreadsheetId,
            isLocked: locked,
            lockedBy: lockedBy,
            sortOrder: sortOrder,
          });
        }
      }
      this.gradingSheets = _.sortBy(this.gradingSheets, "sortOrder");
      for (let i = 0; i < this.gradingSheets.length; i++) {
        for (let j = 0; j < this.gradingSheets[i].inputs.length; j++) {
          if (this.gradingSheets[i].inputs[j].gradingHousekeeping) {
            this.gradingSheets[i].hasHousekeeping = true;
            break;
          }
        }
      }

      this.formGroups = {};
      _.each(this.gradingSheets, (sheet) => {
        this.formGroups[sheet.title] = this.formBuilder.group({});
        _.each(sheet.inputs, (input) => {
          _.each(this.eyes, (eye) => {
            let value;
            if (input.values && input.values[eye]) {
              value = input.values[eye];
            }
            const validators = [];
            if (
              input.gradingRequired &&
              input.gradingInputType !== GradingInputTypes.etdrs
            ) {
              validators.push(Validators.required);
              this.requiredFields[input.spreadsheetId + "-" + input.columnId] =
                true;
            }
            if (
              input.validationType &&
              input.gradingInputType !== GradingInputTypes.etdrs &&
              (input.validationType as string) !== ""
            ) {
              switch (input.validationType) {
                case ValidationTypes.threeDigits:
                  validators.push(ValidateThreeDigit);
                  break;
              }
            }

            // if dropdown, try to convert string to number for display value matching
            const nan = parseInt("NaN", 10);
            if (input.gradingInputType === this.gradingInputTypes.dropdown) {
              const newValue = parseInt(value, 10);
              if (newValue !== nan) {
                value = newValue;
              }
            }

            const control = this.formBuilder.control(value, validators);
            if (
              sheet.isLocked ||
              sheet.status === GradingStagingSheetStatus.complete
            ) {
              control.disable();
            }
            this.formGroups[sheet.title].addControl(
              input.columnId + "-" + eye,
              control
            );
          });
        });
      });
      this.loadingSpinnerService.stop();
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast("Something went wrong: " + err);
    }
  }

  public async saveSheet(
    sheet: GradingSheet,
    submit: boolean,
    buildAnyway?: boolean,
    doNotLock?: boolean,
    confirmed?: boolean
  ) {
    if (submit && !confirmed) {
      this.confirmSubmitModal.open({
        data: {
          sheet: sheet,
          submit: submit,
          buildAnyway: buildAnyway,
          doNotLock: doNotLock,
          confirmed: confirmed,
        },
        headline: "Confirm submission",
        body: `If you have unsaved work on other sheets, SAVE that before you submit this sheet. This includes autofills on Macular Thickness sheet.`,
        yes_text: "Confirm",
        no_text: "Cancel",
      });
      return;
    }

    let missingFields = false;
    let etdrsGridValid = true;
    this.loadingSpinnerService.start();
    let metaDatas: GradingStagingData[] = [];
    _.each(this.eyes, (eye) => {
      _.each(sheet.inputs, (input) => {
        // ETDRS values get expanded x9 and mapped on their own
        if (input.etdrsGroup) {
          const theGrid = this.etdrsGrids.find(
            (e) =>
              e.id === input.spreadsheetId + "-" + input.etdrsGroup + "-" + eye
          );
          if (submit && etdrsGridValid) {
            etdrsGridValid = theGrid.checkGridValidity();
          }
          const etdrsValuesToAdd = theGrid.getValues(this.patientInfo.visitId);
          metaDatas = metaDatas.concat(etdrsValuesToAdd);
        } else {
          metaDatas.push({
            eye: eye,
            value: this.formGroups[sheet.title].get(input.columnId + "-" + eye)
              .value,
            visit: this.patientInfo.visitId,
            metaDataMappingId: input.metaDataMappingId,
          });
        }

        // hacky workaround because the dropdowns don't get caught in the required stuffs
        // this makes dropdowns "obey" required field status
        if (
          submit &&
          this.requiredFields[input.spreadsheetId + "-" + input.columnId]
        ) {
          const field = this.formGroups[sheet.title].get(
            input.columnId + "-" + eye
          );
          if (
            field.value === null ||
            (isNaN(field.value) && isNaN(Date.parse(field.value)))
          ) {
            missingFields = true;
            field.setErrors({ incorrect: true });
            field.markAsTouched();
          }
        }
      });
    });
    this.gradingSheetErrors = {};
    if (submit) {
      let valid = true;
      const formValidity = this.testFormValidity(sheet);
      if (!formValidity.valid) {
        this.gradingSheetErrors[sheet.spreadsheetId] = formValidity.error;
        valid = false;
      } else if (missingFields) {
        this.gradingSheetErrors[sheet.spreadsheetId] =
          "Missing required fields: check OD and OS.";
        valid = false;
      } else if (!etdrsGridValid) {
        this.gradingSheetErrors[sheet.spreadsheetId] = "Invalid ETDRS Grid";
        valid = false;
      }
      if (!valid) {
        this.loadingSpinnerService.stop();
        return;
      }
    }

    try {
      const updatedSheet = await this.gradingService.saveGradingSheet(
        this.patientInfo.patientId,
        sheet.inputs[0].spreadsheetId,
        this.patientInfo.visitId,
        this.studyId,
        metaDatas,
        submit,
        doNotLock
      );
      if (updatedSheet["packageComplete"]) {
        this.router.navigate(["reports"]);
        this.toastService.throwToast(
          sheet.title + " saved successfully. Image package complete"
        );
      } else if (submit) {
        await this.buildPage();
        this.toastService.throwToast(sheet.title + " submitted successfully");
      } else {
        if (buildAnyway) {
          await this.buildPage();
        }
        this.toastService.throwToast(sheet.title + " saved successfully");
      }

      if (submit) {
        // save other sheets
        // TODO dev notes; dropdowns not saving, comments not saving, data not reloading on save
        // ex put data in CF-FFA last two fields, does it try to save the below? does it save the right fields?
        for (let i = 0; i < this.gradingSheets.length; i++) {
          if (
            this.gradingSheets[i].status !== GradingStagingSheetStatus.complete
          ) {
            if (this.sheetHasChanges(this.gradingSheets[i])) {
              console.log("🚀 ~ do extra saveSheet:", this.gradingSheets[i]);

              // double check to make sure there's some non null values
              let hasNonNullValues = false;
              for (let j = 0; j < this.gradingSheets[i].inputs.length; j++) {
                const input = this.gradingSheets[i].inputs[j];
                if (input.values) {
                  for (let val in input.values) {
                    if (
                      input.values[val] !== null &&
                      input.values[val] !== undefined &&
                      input.values[val] !== ""
                    ) {
                      hasNonNullValues = true;
                      break;
                    }
                  }
                }
              }
              if (hasNonNullValues) {
                this.saveSheet(this.gradingSheets[i], false, false, true);
              }
            }
            return;
          }
        }
      }
      this.loadingSpinnerService.stop();
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast(err);
    }
  }

  public async dropdownChanged(
    change: MatSelectChange,
    sheet: GradingSheet,
    columnId: string,
    eye: string,
    isHousekeeping = false
  ) {
    // if housekeeping, copy data to other eyes
    // disabling the dropdown auto-fill do to other eye, but text inputs should still copy over
    // if (isHousekeeping) {
    //   for (let i = 0; i < this.eyes.length; i++) {
    //     const control = this.formGroups[sheet.title].get(columnId + '-' + this.eyes[i]);
    //     control.setValue(change.value);
    //     this.dropdownChanged(change, sheet, columnId, this.eyes[i]);
    //   }
    // }

    const autofills = await this.gradingService.getAutofill(
      sheet.spreadsheetId,
      columnId,
      change.value
    );

    for (let i = 0; i < autofills.length; i++) {
      // each autofill
      // for (let j = 0; j < this.eyes.length; j++) { // each eye

      // if (!isHousekeeping && this.eyes[j] !== eye) {
      //   continue;
      // }

      if (this.formGroups[autofills[i].sheetTitle]) {
        // const control = this.formGroups[autofills[i].sheetTitle].get(autofills[i].columnId + '-' + this.eyes[j]);
        const control = this.formGroups[autofills[i].sheetTitle].get(
          autofills[i].columnId + "-" + eye
        );

        if (control) {
          // if ETDRS call the grid's autofill instead
          if (autofills[i].etdrsGroup) {
            // for (let k = 0; k < this.eyes.length; k++) { // each eye
            const theGrid = this.etdrsGrids.find(
              (e) =>
                // e.id === autofills[i].spreadsheetId + '-' + autofills[i].etdrsGroup + '-' + this.eyes[k] );
                e.id ===
                autofills[i].spreadsheetId +
                  "-" +
                  autofills[i].etdrsGroup +
                  "-" +
                  eye
            );
            if (theGrid) {
              theGrid.populateAllFields(autofills[i].autofillValue);
            }
            // }
          } else {
            control.setValue(autofills[i].autofillValue);
          }
        }
      }
      // }
    }
  }

  public async confirmEscalation(sheet: GradingSheet) {
    try {
      await this.saveSheet(sheet, false);
      await this.gradingService.escalate(
        this.patientInfo.patientId,
        this.patientInfo.visitId,
        sheet.spreadsheetId
      );
      this.toastService.throwToast("Patient successfully escalated");
      this.router.navigate(["reports"]);
    } catch (err) {
      this.toastService.throwToast(err);
    }
  }

  public escalate(sheet: GradingSheet) {
    this.confirmEscalationModal.open({
      data: sheet,
      headline: "Escalate Grading Package?",
      body: `You are about to escalate this entire grading package. If you have not finished grading this subject,
        cancel to finish grading. Include comments as appropriate for senior review in the grading sheets before continuing.
        Your current progress will be automatically saved.`,
      yes_text: "Proceed",
      no_text: "Cancel",
    });
  }

  public housekeepingChange(
    columnId: string,
    eye: string,
    sheetTitle: string,
    value: MatDatepickerInputEvent<any>
  ) {
    for (let i = 0; i < this.eyes.length; i++) {
      if (eye !== this.eyes[i]) {
        if (!value.value) {
          value.value = this.formGroups[sheetTitle].get(
            columnId + "-" + eye
          ).value;
        }
        const control = this.formGroups[sheetTitle].get(
          columnId + "-" + this.eyes[i]
        );
        control.setValue(value.value);
      }
    }
  }

  public testFormValidity(sheet: GradingSheet): {
    valid: boolean;
    error: string;
  } {
    let valid = true;
    let error = "";
    const formGroup: FormGroup = this.formGroups[sheet.title];
    for (const controlName in formGroup.controls) {
      if (formGroup.controls[controlName]) {
        const control = formGroup.controls[controlName];
        if (control.errors) {
          const lastHypenIndex = controlName.lastIndexOf("-");
          const columnId = controlName.substr(0, lastHypenIndex);
          let input: GradingInput;

          for (let i = 0; i < sheet.inputs.length; i++) {
            if (columnId === sheet.inputs[i].columnId) {
              input = sheet.inputs[i];
              break;
            }
          }

          if (input.gradingInputType !== GradingInputTypes.etdrs) {
            valid = false;
            control.markAsTouched();
          }
          error = "Missing required fields: check OD and OS.";
        }
      }
    }

    return { valid: valid, error: error };
  }

  public getTabIndex(
    sheetIndex: number,
    eyeIndex: number,
    inputIndex: number
  ): number {
    let suffix = inputIndex + 1 + "" + 0;
    if (suffix.length < 3) {
      suffix = "0" + suffix;
    }

    const tabIndex = parseInt(
      "1" + sheetIndex + "" + eyeIndex + "" + 0 + "" + suffix,
      10
    );
    return tabIndex;
  }

  public async hasChanges(): Promise<boolean> {
    for (let i = 0; i < this.gradingSheets.length; i++) {
      const sheetHasChanges = await this.sheetHasChanges(this.gradingSheets[i]);
      if (sheetHasChanges) {
        return true;
      }
    }
    return false;
  }

  private async sheetHasChanges(sheet: GradingSheet): Promise<boolean> {
    const oldInputs = this.gradingSheetInputs[sheet.title];

    let metaDatas: GradingStagingData[] = [];
    for (let i = 0; i < this.eyes.length; i++) {
      let eye = this.eyes[i];
      for (let j = 0; j < sheet.inputs.length; j++) {
        let input = sheet.inputs[j];
        // ETDRS values get expanded x9 and mapped on their own
        if (input.etdrsGroup) {
          const theGrid = this.etdrsGrids.find(
            (e) =>
              e.id === input.spreadsheetId + "-" + input.etdrsGroup + "-" + eye
          );
          const etdrsValuesToAdd = theGrid.getValues(this.patientInfo.visitId);
          for (let k = 0; k < etdrsValuesToAdd.length; k++) {
            const oldInput = await _.find(
              oldInputs,
              (oi) =>
                oi.metaDataMappingId === etdrsValuesToAdd[k].metaDataMappingId
            );
            if (oldInput) {
              const oldInputValue = oldInput.values[eye];
              const newInputValue = etdrsValuesToAdd[k].value;
              if (oldInputValue !== newInputValue) {
                if (
                  !oldInputValue &&
                  (!newInputValue || newInputValue == "0")
                ) {
                  // null and 0 default, this is unchanged fields, ignore
                } else {
                  return true;
                }
              }
            }
          }
        } else {
          const oldInput = await _.find(
            oldInputs,
            (oi) => oi.metaDataMappingId === input.metaDataMappingId
          );
          if (oldInput) {
            const oldInputValue = oldInput.values[eye];
            const newInputValue = this.formGroups[sheet.title].get(
              input.columnId + "-" + eye
            ).value;
            if (oldInputValue !== newInputValue) {
              if (!oldInputValue && (!newInputValue || isNaN(newInputValue))) {
                // null and not a number, this is unchanged fields, ignore
              } else {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  }

  sheetClicked(sheet: GradingSheet) {
    const group = this.sheetGroups.find((group) => {
      return group.nativeElement.id === sheet.title + "-sheetGroup";
    });
    group.nativeElement.scrollIntoView({ behavior: "smooth" });

    for (let i = 0; i < this.gradingSheets.length; i++) {
      if (this.gradingSheets[i].spreadsheetId === sheet.spreadsheetId) {
        this.gradingSheets[i].isExpanded = !this.gradingSheets[i].isExpanded;
      }
    }
  }
}

export class AutofillInstructions {
  sheetTitle: string;
  spreadsheetId: string;
  columnId: string;
  autofillValue: number;
  etdrsGroup?: string;
}

enum EyeNameLookup {
  OD = "Right",
  OS = "Left",
}

// field must be a 1-3 digit integer
function ValidateThreeDigit(control: AbstractControl) {
  if (control.value == null || isNaN(control.value)) {
    return { nan: true, valid: false };
  }
  if (control.value % 1 === 0 && control.value >= 0 && control.value <= 999) {
    return null;
  } else {
    return { outofrange: true, valid: false };
  }
}



// WEBPACK FOOTER //
// ./src/app/home/grading/grading/grading.component.ts